import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import Step1AdditionalNight from './Step1AdditionalNight';
import Step2PassengerData from './Step2PassengerData';
import Step3ContactData from './Step3ContactData';
import Step4TravelItinerary from './Step4TravelItinerary';
import Step5HotelTransfer from './Step5HotelTransfer';
import Step6EmergencyContact from './Step6EmergencyContact';
import { stepValidators, extractStepData } from '../../utils/validationUtils';
import { matchesVariant } from '../../utils/variantUtils';

/**
 * BookingModal - Componente principal del modal de reserva con formulario multi-step
 *
 * Props:
 * - isOpen: Boolean indicando si el modal está abierto
 * - onClose: Función callback para cerrar el modal
 * - product: Objeto del producto principal
 * - allProducts: Array de todos los productos disponibles
 * - selectedVariants: Objeto con variantes seleccionadas { hotel: "...", room: "..." }
 * - onSubmit: Función callback (paymentData) => Promise<void> para manejar el submit final
 * -IMPORTANT: ONLY USE PRICE COP, THE PRICE USD IS NOT USED
 */
function BookingModal({
  isOpen,
  onClose,
  product,
  allProducts,
  selectedVariants,
  onSubmit
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find additional product that matches selected variants
  const additionalProduct = useMemo(() => {
    return allProducts.find(p => {
      if (!p.isAdditional || !p.variantData) {
        return false;
      }

      // Check if the additional product contains ALL the selected variants
      return Object.entries(selectedVariants).every(([key, value]) => {
        if (!value) return true; // Skip empty values
        return matchesVariant(p.variantData, key, value);
      });
    });
  }, [allProducts, selectedVariants]);

  // Determine initial step based on whether additional product exists
  const initialStep = useMemo(() => {
    return additionalProduct ? 1 : 2;
  }, [additionalProduct]);

  const [currentStep, setCurrentStep] = useState(initialStep);

  // Calculate total price (base product × maxPersons)
  const totalPrice = {
    usd: product.priceUsd,
    cop: product.priceCop
  };

  // Form state usando useState
  const [formData, setFormData] = useState(() => {
    return {
      // Step 1: Additional Night
      includeAdditionalNight: false,

      // Step 2: Passengers
      passengers: Array.from({ length: product.maxPersons }, () => ({
        lastName: '',
        firstName: '',
        birthDate: '',
        idType: 'cedula',
        idNumber: '',
        nationality: ''
      })),

      // Step 3: Contact
      email: '',
      confirmEmail: '',
      phone: '',

      // Step 4: Travel (optional)
      arrivalDate: '',
      arrivalAirlineFlight: '',
      departureDate: '',
      departureAirlineFlight: '',

      // Step 5: Hotel Transfer
      hotelTransfer: {
        needsTransfer: 'SI',
        numberOfTickets: product.maxPersons.toString()
      },

      // Step 6: Emergency Contacts (commented out in original)
      emergencyContacts: Array.from({ length: product.maxPersons }, () => ({
        name: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        relationship: 'familiar'
      }))
    };
  });

  // Errors state
  const [errors, setErrors] = useState({});

  // Validate current step
  const validateCurrentStep = () => {
    const stepData = extractStepData(formData, currentStep);

    // For step 2, filter passengers based on product.maxPersons
    if (currentStep === 2) {
      stepData.passengers = stepData.passengers.slice(0, product.maxPersons);
    }

    const validator = stepValidators[currentStep];
    if (!validator) {
      return { isValid: true, errors: {} };
    }

    const result = validator(stepData);

    if (!result.isValid) {
      setErrors(result.errors);
    } else {
      setErrors({});
    }

    return result;
  };

  // Validate all steps before final submission
  const validateAllSteps = () => {
    let firstErrorStep = null;
    const allErrors = {};

    // Check each step from initial step to 5 (or 6 if emergency contacts enabled)
    for (let step = initialStep; step <= 5; step++) {
      let stepData = extractStepData(formData, step);

      // For step 2, filter passengers based on product.maxPersons
      if (step === 2) {
        stepData.passengers = stepData.passengers.slice(0, product.maxPersons);
      }

      const validator = stepValidators[step];
      if (!validator) continue;

      const result = validator(stepData);

      if (!result.isValid) {
        // Mark this as the first error step if we haven't found one yet
        if (!firstErrorStep) {
          firstErrorStep = step;
        }

        // Merge errors
        Object.assign(allErrors, result.errors);
      }
    }

    const isValid = !firstErrorStep;

    return { isValid, firstErrorStep, errors: allErrors };
  };

  // Navigation functions
  const nextStep = () => {
    const validation = validateCurrentStep();
    if (validation.isValid && currentStep < 5) { // Change to 6 if using emergency contacts
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > initialStep) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
    }
  };

  // Handle form field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handlePassengerChange = (index, fieldName, value) => {
    setFormData(prev => {
      const newPassengers = [...prev.passengers];
      
      // Asegurar que el pasajero existe con valores por defecto antes de actualizar
      const currentPassenger = newPassengers[index] || {
        lastName: '',
        firstName: '',
        birthDate: '',
        idType: 'cedula',
        idNumber: '',
        nationality: ''
      };
      
      newPassengers[index] = {
        ...currentPassenger,
        [fieldName]: value
      };
      
      return {
        ...prev,
        passengers: newPassengers
      };
    });
  };

  const handleHotelTransferChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      hotelTransfer: {
        ...prev.hotelTransfer,
        [fieldName]: value
      }
    }));
  };

  const handleEmergencyContactChange = (index, fieldName, value) => {
    setFormData(prev => {
      const newContacts = [...prev.emergencyContacts];
      newContacts[index] = {
        ...newContacts[index],
        [fieldName]: value
      };
      return {
        ...prev,
        emergencyContacts: newContacts
      };
    });
  };

  // Map ID type from form to backend format
  const mapIdTypeToBackend = (idType) => {
    switch (idType) {
      case 'cedula':
        return 'cedula';
      case 'pasaporte':
        return 'pasaporte';
      case 'extranjeria':
        return 'cedula_extranjeria';
      default:
        return 'cedula';
    }
  };

  // Map form data to Reserva structure (backend format)
  const mapToPaymentData = (data) => {
    // Generate reference (in real app, this would be from backend)
    const reference = `BQ-${Date.now()}`;

    // Calculate final amounts including additional product if selected
    const additionalAmount = data.includeAdditionalNight && additionalProduct ? {
      usd: additionalProduct.priceUsd,
      cop: additionalProduct.priceCop
    } : { usd: 0, cop: 0 };

    const finalAmount = {
      usd: totalPrice.usd + additionalAmount.usd,
      cop: totalPrice.cop + additionalAmount.cop
    };

    return {
      // Contact information (Spanish field names as expected by backend)
      emailContacto: data.email,
      nombreContacto: data.passengers[0]?.firstName && data.passengers[0]?.lastName
        ? `${data.passengers[0].firstName} ${data.passengers[0].lastName}`
        : data.email,
      telefonoContacto: data.phone,

      // Passengers array (Spanish field names)
      pasajeros: data.passengers
        .filter((passenger) =>
          passenger.firstName &&
          passenger.lastName &&
          passenger.birthDate &&
          passenger.idType &&
          passenger.idNumber
        )
        .map((passenger, index) => ({
          nombres: passenger.firstName,
          apellidos: passenger.lastName,
          fechaNacimiento: passenger.birthDate, // Keep as YYYY-MM-DD string format
          tipoDocumento: mapIdTypeToBackend(passenger.idType),
          numeroDocumento: passenger.idNumber,
          nacionalidad: passenger.nationality,
          contactoEmergencia: data.emergencyContacts[index]?.name || '',
          telefonoEmergencia: data.emergencyContacts[index]?.phone || ''
        })),

      // Product and variant info (will be completed in Pricing.js)
      // producto: will be set in Pricing.js
      // nombreVariante: will be set in Pricing.js

      // Total amount
      total: finalAmount.cop,

      // Reference
      reference,

      // Travel itinerary (flat fields, not nested)
      fechaLlegada: data.arrivalDate || null,
      aerolineaVueloLlegada: data.arrivalAirlineFlight || '',
      fechaSalida: data.departureDate || null,
      aerolineaVueloSalida: data.departureAirlineFlight || '',

      // Hotel transfer (flat fields, converted types)
      necesitaTraslado: data.hotelTransfer.needsTransfer === 'SI',
      numeroTicketsTraslado: parseInt(data.hotelTransfer.numberOfTickets) || 0,

      // Additional data for ePayco payment (not sent to backend)
      _paymentData: {
        amountUsd: finalAmount.usd,
        amountCop: finalAmount.cop,
        productId: product.id,
        additionalProductId: data.includeAdditionalNight && additionalProduct ? additionalProduct.documentId : null
      }
    };
  };

  // Handle final form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // First, validate all steps before proceeding
      const validation = validateAllSteps();

      if (!validation.isValid) {
        setIsSubmitting(false);
        setErrors(validation.errors);
        // Navigate to the first step with errors
        if (validation.firstErrorStep) {
          setCurrentStep(validation.firstErrorStep);
        }
        return;
      }

      console.log('✅ All steps validated successfully, proceeding with payment...');

      // Map to Payment structure
      const paymentData = mapToPaymentData(formData);

      // Call the onSubmit callback with product info
      await onSubmit(paymentData, product);

      // Reset form and close modal
      handleClose();
    } catch (error) {
      console.error('Error submitting booking:', error);
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      setCurrentStep(initialStep);
      setErrors({});
      // Reset form data
      setFormData({
        includeAdditionalNight: false,
        passengers: Array.from({ length: product.maxPersons }, () => ({
          lastName: '',
          firstName: '',
          birthDate: '',
          idType: 'cedula',
          idNumber: '',
          nationality: ''
        })),
        email: '',
        confirmEmail: '',
        phone: '',
        arrivalDate: '',
        arrivalAirlineFlight: '',
        departureDate: '',
        departureAirlineFlight: '',
        hotelTransfer: {
          needsTransfer: 'SI',
          numberOfTickets: product.maxPersons.toString()
        },
        emergencyContacts: Array.from({ length: product.maxPersons }, () => ({
          name: '',
          address: '',
          city: '',
          country: '',
          phone: '',
          relationship: 'familiar'
        }))
      });
      onClose();
    }
  };

  // Reset to initial step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(initialStep);
    }
  }, [isOpen, initialStep]);

  // Reactstrap Modal handles ESC key and body scroll automatically

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (additionalProduct) {
          return (
            <Step1AdditionalNight
              product={product}
              additionalProduct={additionalProduct}
              selectedVariants={selectedVariants}
              totalPrice={totalPrice}
              includeAdditionalNight={formData.includeAdditionalNight}
              onChange={(value) => handleFieldChange('includeAdditionalNight', value)}
              onContinue={nextStep}
            />
          );
        }
        // Fallthrough to step 2 if no additional product
        return (
          <Step2PassengerData
            maxPersons={product.maxPersons}
            passengers={formData.passengers}
            onChange={handlePassengerChange}
            errors={errors}
            onBack={currentStep > initialStep ? prevStep : undefined}
            onContinue={nextStep}
          />
        );
      case 2:
        return (
          <Step2PassengerData
            maxPersons={product.maxPersons}
            passengers={formData.passengers}
            onChange={handlePassengerChange}
            errors={errors}
            onBack={currentStep > initialStep ? prevStep : undefined}
            onContinue={nextStep}
          />
        );
      case 3:
        return (
          <Step3ContactData
            email={formData.email}
            confirmEmail={formData.confirmEmail}
            phone={formData.phone}
            onChange={handleFieldChange}
            errors={errors}
            onBack={prevStep}
            onContinue={nextStep}
          />
        );
      case 4:
        return (
          <Step4TravelItinerary
            arrivalDate={formData.arrivalDate}
            arrivalAirlineFlight={formData.arrivalAirlineFlight}
            departureDate={formData.departureDate}
            departureAirlineFlight={formData.departureAirlineFlight}
            onChange={handleFieldChange}
            errors={errors}
            onBack={prevStep}
            onContinue={nextStep}
            isLoading={isSubmitting}
          />
        );
      case 5:
        return (
          <Step5HotelTransfer
            maxPersons={product.maxPersons}
            hotelTransfer={formData.hotelTransfer}
            onChange={handleHotelTransferChange}
            errors={errors}
            onBack={prevStep}
            onContinue={nextStep}
            isLoading={isSubmitting}
          />
        );
      case 6:
        return (
          <Step6EmergencyContact
            maxPersons={product.maxPersons}
            emergencyContacts={formData.emergencyContacts}
            onChange={handleEmergencyContactChange}
            errors={errors}
            onBack={prevStep}
            onContinue={nextStep}
            isLoading={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={!isSubmitting ? handleClose : undefined}
      size="lg"
      backdrop={!isSubmitting ? true : 'static'}
      keyboard={!isSubmitting}
      centered
    >
      <ModalBody className="p-4 p-md-5">
        <form onSubmit={handleSubmit}>
          {renderCurrentStep()}
        </form>
      </ModalBody>
    </Modal>
  );
}

export default BookingModal;
